import React, { createContext, useState } from "react";

export const AccessibilityContext = createContext<AccessibilityContextType | null>(null);

// Accessibility context type
export interface AccessibilityContextType {
  fontSize: number;
  filterBW: boolean;
  invert: boolean;
  negative: boolean;
  linkHighlight: boolean;
  hideImages: boolean;
  setFontSize: (value: number | ((prev: number) => number)) => void;
  setFilterBW: (value: boolean | ((prev: boolean) => boolean)) => void;
  setInvert: (value: boolean | ((prev: boolean) => boolean)) => void;
  setNegative: (value: boolean | ((prev: boolean) => boolean)) => void;
  setLinkHighlight: (value: boolean | ((prev: boolean) => boolean)) => void;
  setHideImages: (value: boolean | ((prev: boolean) => boolean)) => void;
  getAccessibilityClasses: () => string;
  getAccessibilityStyles: () => React.CSSProperties;
}


interface AccessibilityProviderProps {
  children: React.ReactNode;
}

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({ children }) => {
  const [fontSize, setFontSize] = useState<number>(16);
  const [filterBW, setFilterBW] = useState<boolean>(false);
  const [invert, setInvert] = useState<boolean>(false);
  const [negative, setNegative] = useState<boolean>(false);
  const [linkHighlight, setLinkHighlight] = useState<boolean>(false);
  const [hideImages, setHideImages] = useState<boolean>(false);

  const getAccessibilityClasses = () => {
    return `${filterBW ? "black-white" : ""} 
    ${invert ? "invert" : ""} 
    ${negative ? "negative" : ""} 
    ${linkHighlight ? "linkHighlight" : ""}
    ${hideImages ? "hideImages" : ""} `;
  };

  const getAccessibilityStyles = () => {
    return {
      fontSize: `${fontSize}px`
    };
  };

  const contextValue: AccessibilityContextType = {
    fontSize,
    filterBW,
    invert,
    negative,
    linkHighlight,
    hideImages,
    setFontSize,
    setFilterBW,
    setInvert,
    setNegative,
    setLinkHighlight,
    setHideImages,
    getAccessibilityClasses,
    getAccessibilityStyles
  };

  return (
    <AccessibilityContext.Provider value={contextValue}>
      {children}
    </AccessibilityContext.Provider>
  );
};

// Hook to use accessibility context
export const useAccessibility = (): AccessibilityContextType => {
  const context = React.useContext(AccessibilityContext);
  if (!context) {
    throw new Error("useAccessibility must be used within an AccessibilityProvider");
  }
  return context;
};
